import { LitElement, html, css } from 'lit'
import { navigate } from '../router.js'
import { STATUS_LABEL, STATUS_STYLE, formatDate } from '../status.js'

// One row in the ticket landing list (§7.3). Presentational — takes a ticket DTO.
// Restyled to the BrandExtract card row: a status-colored spine, subject/summary,
// and a right rail with the status badge, the assignee (or the group queue holding
// an unassigned ticket), and the opener.
class TicketRow extends LitElement {
  static properties = { ticket: { type: Object } }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); }
    .card { display: flex; align-items: stretch; overflow: hidden; cursor: pointer;
            background: var(--white); border-radius: var(--radius-card); box-shadow: var(--shadow-row);
            transition: box-shadow .16s ease, transform .16s ease; }
    .card:hover { box-shadow: 0 1px 2px rgba(51,35,75,.05), 0 14px 34px rgba(51,35,75,.10); transform: translateY(-1px); }
    .spine { width: 4px; flex-shrink: 0; }
    .body { flex: 1; display: grid; grid-template-columns: 88px 1fr auto; gap: 0 24px; align-items: center; padding: 20px 24px; }
    .num { font-family: var(--font-eyebrow); font-size: 14px; font-weight: 500; color: var(--neutral-500); font-variant-numeric: tabular-nums; }
    .client { font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
              text-transform: uppercase; color: var(--sky-500); margin-bottom: 5px; }
    .subject { font-size: 19px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.25; color: var(--eggplant-500); }
    .summary { font-size: 14px; color: var(--neutral-600); margin-top: 5px; line-height: 1.45;
               overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 58ch; }
    .rail { display: flex; flex-direction: column; align-items: flex-end; gap: 10px; text-align: right; }
    .badge { display: inline-flex; align-items: center; padding: 5px 12px; border-radius: 999px;
             font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
             text-transform: uppercase; white-space: nowrap; }
    .assignee { display: flex; align-items: center; gap: 9px; }
    .assignee .who { font-size: 13px; font-weight: 600; color: var(--eggplant-500); }
    .unassigned { font-size: 13px; font-weight: 600; color: var(--chartreuse-700); }
    .avatar { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; display: grid; place-items: center;
              background: var(--eggplant-100); color: var(--eggplant-500); font-weight: 700; font-size: 10px; }
    /* A group queue is not a person: squared tile, sky tint, and a "Group" qualifier
       so the name is never read as the agent who owns the ticket. */
    .avatar.team { border-radius: var(--radius-sm); background: var(--sky-100); color: var(--sky-700); }
    .assignee .qualifier { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700;
                           letter-spacing: 0.06em; text-transform: uppercase; color: var(--neutral-500); }
    .opener { font-size: 12px; color: var(--neutral-500); }
    @media (max-width: 720px) {
      .body { grid-template-columns: 1fr; gap: 12px; }
      .rail { align-items: flex-start; text-align: left; }
    }
  `

  _initials(person) {
    const src = (person?.name || person?.email || '?').trim()
    return src.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
  }

  render() {
    const t = this.ticket
    if (!t) return html``
    const summary = t.aiSummary || t.subject
    const s = STATUS_STYLE[t.status] || STATUS_STYLE.closed
    return html`
      <div class="card" @click=${() => navigate(`/tickets/${t.number}`)}>
        <div class="spine" style="background:${s.bg}"></div>
        <div class="body">
          <span class="num">#${t.number}</span>
          <div style="min-width:0">
            ${t.clientName ? html`<div class="client">${t.clientName}</div>` : ''}
            <div class="subject">${t.subject}</div>
            <div class="summary">${summary}</div>
          </div>
          <div class="rail">
            <span class="badge" style="background:${s.bg};color:${s.fg}">${STATUS_LABEL[t.status]}</span>
            <div class="assignee">
              ${t.assignee
                ? html`<span class="avatar">${this._initials(t.assignee)}</span>
                       <span class="who">${t.assignee.name || t.assignee.email}</span>`
                : t.group
                  // No agent owns it yet, but it sits in a group queue (§7.7) — name the
                  // queue rather than saying "Unassigned", which hides the routing.
                  ? html`<span class="avatar team">${this._initials(t.group)}</span>
                         <span class="who">${t.group.name} <span class="qualifier">Group</span></span>`
                  : html`<span class="unassigned">Unassigned</span>`}
            </div>
            <div class="opener">Opened by ${t.requester ? t.requester.name || t.requester.email : '—'} · ${formatDate(t.openDate)}</div>
          </div>
        </div>
      </div>
    `
  }
}

customElements.define('ticket-row', TicketRow)
